<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { ImageEditForm } from '@client/pages/manage/assets/edit/edit-image/shared';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  import { formatBytes } from '@common/utils/numbers';
  import { deepCopy } from '@common/utils/objects';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let images = [];
  let selectedImage = null;
  let image = null;
  let originalImage = null;
  let mimeType = '';
  let invalidFilename = false;

  const loadImages = async () => {
    const response = await AssetsService.getAllImages();
    images = response.map((img) => ({
      label: img.filename,
      value: img.filename,
      ...img
    }));
  };

  const onSelectImage = (event) => {
    selectedImage = event.detail;
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex === 0 && selectedImage) {
      originalImage = deepCopy(selectedImage);
      image = deepCopy(selectedImage);
      currentInternalStep = 1;
      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      const response = await AssetsService.updateImage(image);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.UPDATE', { values: { item: image.filename } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_IMAGE'));
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: image.filename } }));

      step.data = {
        id: image.filename,
        rollbackData: {
          originalImage
        }
      };

      return response;
    } catch (error) {
      notify($format('label.FAILURE.UPDATE', { values: { item: image.filename } }), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    $appStore.loading = true;

    try {
      const { originalImage } = step.data.rollbackData;

      const response = await AssetsService.updateImage(originalImage);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK_IMAGE_UPDATE'), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_IMAGE_UPDATE'));
      }

      notify($format('label.SUCCESS.ROLLBACK_IMAGE_UPDATE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_IMAGE_UPDATE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const hasChanges = () => {
    if (!originalImage || !image) return false;
    return JSON.stringify(originalImage) !== JSON.stringify(image);
  };

  onMount(async () => {
    loading = true;

    try {
      await loadImages();
    } catch (error) {
      console.error('Failed to load images:', error);
      notify($format('label.FAILURE.LOAD_IMAGES'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      nextDisabled = true; // Always disabled on step 0 (selection step)
    } else if (currentInternalStep === 1) {
      nextDisabled = !hasChanges() || !image?.filename || invalidFilename;
    } else {
      nextDisabled = true;
    }
  }
</script>

<div class="image-update-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.SELECT_IMAGE')}
    summary={selectedImage?.filename || $format('label.NOT_SELECTED')}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={currentInternalStep === 0 && !selectedImage}
  >
    {#if loading}
      <div class="loading">
        <Spinner show inline />
      </div>
    {:else}
      <SectionItem label={$format('label.IMAGE')} description={$format('label.CHOOSE_IMAGE_TO_UPDATE')}>
        <Select
          items={images}
          bind:value={selectedImage}
          placeholder={$format('label.SELECT_IMAGE')}
          clearable={false}
          showChevron
          on:change={onSelectImage}
        >
          <svelte:fragment slot="item" let:item>
            <div class="item">
              <div class="item-detail">
                <div class="image">
                  <img
                    src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                    style="background: url({item.imageurl}) no-repeat center center;"
                    alt={item.filename}
                    loading="lazy"
                  />
                </div>
                <div class="info">
                  <b>{item.filename}</b>
                  <small>{$format('label.FILE_SIZE')} {formatBytes(item.filesize)}</small>
                </div>
              </div>
            </div>
          </svelte:fragment>
        </Select>
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.UPDATE_IMAGE')}
    summary={image?.filename || $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={currentInternalStep === 1 && (!hasChanges() || !image?.filename || invalidFilename)}
  >
    {#if image}
      <ImageEditForm bind:data={image} bind:mimeType bind:invalidFilename />
    {/if}
  </Step>
</div>

<style lang="scss">
  .image-update-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-xlarge);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }

    .item-detail {
      align-items: center;
      display: block;

      .info {
        align-items: start;
        display: flex;
        flex-direction: column;
        justify-content: center;

        b {
          color: var(--color-gray-900);
          text-align: center;
          text-wrap: wrap;
        }

        small {
          color: var(--color-gray-600);
          display: block;
          margin-top: var(--margin-xsmall);
        }
      }

      .image {
        align-items: start;
        display: flex;
        justify-content: start;
        margin-bottom: var(--margin-large);

        img {
          background-size: contain !important;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-base);
          display: block;
          height: 120px;
          width: auto;
        }
      }
    }

    .placeholder {
      padding: var(--padding-xlarge);
      text-align: center;
      color: var(--color-gray-600);

      p {
        margin-bottom: var(--margin-small);

        &:last-child {
          margin-bottom: 0;
        }
      }

      .text-muted {
        font-size: var(--font-size-small);
      }
    }
  }
</style>
